import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import type { AdminRecentJobRow } from '../../../types/admin_job_hub';
import type { Job } from '../../../types/job';

type RpcJobRow = { item: Job; total_count: number };

export default function AdminRecentJobsHubSection() {
  const { t } = useTranslation(['admin', 'common']);
  const [jobs, setJobs] = useState<AdminRecentJobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('admin_fetch_jobs', {
      p_limit: 5,
      p_offset: 0,
      p_status: null,
      p_visibility: null,
      p_search: null,
    });
    if (rpcError) {
      setError(rpcError.message);
      setJobs([]);
      setLoading(false);
      return;
    }
    const rows = (data ?? []) as RpcJobRow[];
    setJobs(
      rows.map(r => ({
        id: r.item.id,
        title: r.item.title,
        status: r.item.status,
        created_at: r.item.created_at,
      }))
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('jobActivityHub.dashboard_section_title', { ns: 'admin' })}
        </h2>
        <Link
          to="/admin/jobs"
          className="text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          {t('jobActivityHub.view_all_jobs', { ns: 'admin' })}
        </Link>
      </div>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {t('jobActivityHub.dashboard_section_subtitle', { ns: 'admin' })}
      </p>

      {error ? (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="mt-4 text-sm text-gray-500">{t('loading', { ns: 'common' })}</p>
      ) : jobs.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">
          {t('jobActivityHub.no_recent_jobs', { ns: 'admin' })}
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100 dark:divide-white/[0.06]">
          {jobs.map(job => (
            <li key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900 dark:text-gray-100">{job.title}</p>
                <p className="text-xs text-gray-500 capitalize dark:text-gray-400">{job.status}</p>
              </div>
              <Link
                to={`/admin/jobs/${job.id}/hub`}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-gray-800 transition hover:bg-gray-100 dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-gray-100 dark:hover:bg-white/[0.1]"
              >
                <Activity size={16} />
                {t('jobActivityHub.open_hub', { ns: 'admin' })}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
